import React, { useState, useEffect } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Plus, Search, UserCog, Palette, Eye, Power, Wifi, WifiOff, Loader2, Trash2, Zap, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminClients() {
  const { getAdminAxios } = useOutletContext();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newClient, setNewClient] = useState({ email: "", password: "", name: "", business_name: "" });
  const [toggling, setToggling] = useState(null);
  const [dangerAction, setDangerAction] = useState(null); // {type, client, confirmText}
  const [dangerConfirmText, setDangerConfirmText] = useState("");
  const [running, setRunning] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const fetchClients = () => {
    getAdminAxios().get(`${API}/admin/clients`)
      .then(({ data }) => setClients(data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchClients(); }, []);

  const handleCreate = async () => {
    if (!newClient.email || !newClient.password || !newClient.name) return;
    setCreating(true);
    try {
      await getAdminAxios().post(`${API}/admin/clients`, newClient);
      setShowCreate(false);
      setNewClient({ email: "", password: "", name: "", business_name: "" });
      fetchClients();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to create client");
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (clientId) => {
    setToggling(clientId);
    try {
      await getAdminAxios().put(`${API}/admin/clients/${clientId}/toggle-active`);
      fetchClients();
    } catch (err) {
      console.error(err);
    } finally {
      setToggling(null);
    }
  };

  const openDanger = (type, client) => {
    setDangerConfirmText("");
    setDangerAction({ type, client });
  };

  const runDanger = async () => {
    if (!dangerAction) return;
    const { type, client } = dangerAction;
    setRunning(true);
    try {
      if (type === "delete") {
        const { data } = await getAdminAxios().delete(`${API}/admin/clients/${client.id}`);
        setFeedback({ ok: true, msg: `Cliente eliminado: ${data.email || client.email}` });
      } else if (type === "clear-cache") {
        const { data } = await getAdminAxios().post(`${API}/admin/clients/${client.id}/clear-whatsapp-cache`);
        setFeedback({ ok: true, msg: `Caché borrado. Liberados: ${data.freed_human || "0 B"}` });
      }
      fetchClients();
    } catch (err) {
      setFeedback({ ok: false, msg: err.response?.data?.detail || err.message });
    } finally {
      setRunning(false);
      setDangerAction(null);
      setDangerConfirmText("");
    }
  };

  const filtered = clients.filter((c) =>
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.email?.toLowerCase().includes(search.toLowerCase()) ||
    c.business_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-8" data-testid="admin-clients-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-white">Client Management</h1>
          <p className="text-sm text-slate-400 mt-1">Manage all client accounts and workspaces</p>
        </div>
        <Button className="bg-amber-500 hover:bg-amber-600 text-black font-medium" onClick={() => setShowCreate(true)} data-testid="add-client-button">
          <Plus className="w-4 h-4 mr-2" /> Add Client
        </Button>
      </div>

      <div className="relative mb-6 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <Input
          placeholder="Search clients..." value={search} onChange={(e) => setSearch(e.target.value)}
          className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-slate-500"
          data-testid="search-clients"
        />
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <div key={i} className="h-20 bg-white/5 rounded-xl animate-pulse" />)}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="bg-[#13112b] border-indigo-900/30">
          <CardContent className="py-12 text-center">
            <p className="text-slate-400">{clients.length === 0 ? "No clients yet" : "No matching clients"}</p>
          </CardContent>
        </Card>
      ) : (
        <ScrollArea className="h-[calc(100vh-240px)]">
          <div className="space-y-3 pr-2">
            {filtered.map((client) => (
              <Card key={client.id} className="bg-[#13112b] border-indigo-900/30 hover:border-indigo-800/50 transition-colors" data-testid={`client-row-${client.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="w-10 h-10 rounded-full bg-indigo-500/20 flex items-center justify-center shrink-0">
                        <span className="text-sm font-bold text-indigo-300">{(client.name || "?")[0].toUpperCase()}</span>
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-white truncate">{client.name}</p>
                          <Badge className={client.is_active ? "bg-emerald-500/20 text-emerald-400 border-0 text-[10px]" : "bg-red-500/20 text-red-400 border-0 text-[10px]"}>
                            {client.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-400 truncate">{client.email}</p>
                        {client.business_name && <p className="text-xs text-slate-500 truncate">{client.business_name}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 shrink-0">
                      <div className="flex items-center gap-1.5">
                        {client.wa_status === "connected" ? (
                          <><Wifi className="w-3.5 h-3.5 text-green-400" /><span className="text-xs text-green-400">Connected</span></>
                        ) : (
                          <><WifiOff className="w-3.5 h-3.5 text-slate-500" /><span className="text-xs text-slate-500">{client.wa_status?.replace(/_/g, " ") || "Not connected"}</span></>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-amber-400 hover:bg-amber-500/10" onClick={() => openDanger("clear-cache", client)} title="Borrar caché de WhatsApp" data-testid={`clear-cache-${client.id}`}>
                          <Zap className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-amber-400 hover:bg-amber-500/10" onClick={() => handleToggleActive(client.id)} disabled={toggling === client.id} data-testid={`toggle-active-${client.id}`}>
                          {toggling === client.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Power className="w-4 h-4" />}
                        </Button>
                        <Link to={`/admin/clients/${client.id}`}>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-white hover:bg-white/10" data-testid={`view-client-${client.id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                        </Link>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-400 hover:bg-red-500/10" onClick={() => openDanger("delete", client)} title="Eliminar cliente" data-testid={`delete-client-${client.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </ScrollArea>
      )}

      {feedback && (
        <div className={`mb-4 p-3 rounded-lg border text-sm ${feedback.ok ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-200" : "bg-red-500/10 border-red-500/30 text-red-200"}`}
             data-testid="action-feedback">
          {feedback.msg}
          <button className="ml-3 text-xs underline opacity-70" onClick={() => setFeedback(null)}>cerrar</button>
        </div>
      )}

      {/* Create Client Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[#13112b] border-indigo-900/30 text-white" data-testid="create-client-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Add New Client</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-slate-300">Full Name</Label>
              <Input value={newClient.name} onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} placeholder="John Doe" className="bg-white/5 border-white/10 text-white" data-testid="new-client-name" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Email</Label>
              <Input value={newClient.email} onChange={(e) => setNewClient({ ...newClient, email: e.target.value })} placeholder="client@example.com" type="email" className="bg-white/5 border-white/10 text-white" data-testid="new-client-email" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Password</Label>
              <Input value={newClient.password} onChange={(e) => setNewClient({ ...newClient, password: e.target.value })} placeholder="Min 6 characters" type="password" className="bg-white/5 border-white/10 text-white" data-testid="new-client-password" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Business Name (optional)</Label>
              <Input value={newClient.business_name} onChange={(e) => setNewClient({ ...newClient, business_name: e.target.value })} placeholder="Acme Corp" className="bg-white/5 border-white/10 text-white" data-testid="new-client-business" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} className="border-white/10 text-slate-300 hover:bg-white/5">Cancel</Button>
            <Button onClick={handleCreate} disabled={creating} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="create-client-submit">
              {creating ? "Creating..." : "Create Client"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Danger Confirmation Dialog */}
      <Dialog open={!!dangerAction} onOpenChange={(o) => { if (!o) { setDangerAction(null); setDangerConfirmText(""); } }}>
        <DialogContent className="bg-[#13112b] border-red-900/40 text-white" data-testid="danger-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              {dangerAction?.type === "delete" ? "Eliminar cliente" : "Borrar caché de WhatsApp"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {dangerAction?.type === "delete" ? (
              <>
                <p className="text-sm text-slate-300">
                  Vas a <b className="text-red-400">eliminar permanentemente</b> al cliente <b>{dangerAction?.client?.email}</b> junto con su workspace, mensajes, conversaciones, flujos, comandos, configuración del bot y caché de WhatsApp. <span className="text-red-400 font-semibold">Esta acción no se puede deshacer.</span>
                </p>
                <div className="space-y-2">
                  <Label className="text-slate-300 text-xs">Escribe <span className="font-mono bg-white/5 px-1.5 py-0.5 rounded text-red-400">DELETE</span> para confirmar</Label>
                  <Input value={dangerConfirmText} onChange={(e) => setDangerConfirmText(e.target.value)}
                         placeholder="DELETE" className="bg-white/5 border-white/10 text-white"
                         data-testid="delete-confirm-input" />
                </div>
              </>
            ) : (
              <p className="text-sm text-slate-300">
                Se cerrará la sesión de WhatsApp de <b>{dangerAction?.client?.email}</b> y se borrarán los archivos de Baileys en disco. El cliente deberá volver a escanear el QR. ¿Continuar?
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDangerAction(null); setDangerConfirmText(""); }}
                    disabled={running}
                    className="border-white/10 text-slate-300 hover:bg-white/5">Cancelar</Button>
            <Button onClick={runDanger}
                    disabled={running || (dangerAction?.type === "delete" && dangerConfirmText !== "DELETE")}
                    className={dangerAction?.type === "delete" ? "bg-red-500 hover:bg-red-600 text-white" : "bg-amber-500 hover:bg-amber-600 text-black"}
                    data-testid="danger-confirm-btn">
              {running && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {dangerAction?.type === "delete" ? "Eliminar definitivamente" : "Borrar caché"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
